import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import Aurora from '../components/Aurora';
import Logo from '../components/Logo';
import { FaEye, FaEyeSlash, FaCheck } from 'react-icons/fa';

const ResetPassword: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<{
    password?: boolean;
    confirmPassword?: boolean;
  }>({});

  useEffect(() => {
    requestAnimationFrame(() => setMounted(true));
  }, []);

  // Password strength
  const passwordStrength = (() => {
    if (!password) return 0;
    let s = 0;
    if (password.length >= 6) s++;
    if (password.length >= 10) s++;
    if (/[A-Z]/.test(password) && /[a-z]/.test(password)) s++;
    if (/[0-9]/.test(password)) s++;
    if (/[^A-Za-z0-9]/.test(password)) s++;
    return s;
  })();

  const strengthLabel = ['', 'Weak', 'Fair', 'Good', 'Strong', 'Excellent'][passwordStrength];
  const strengthColor = ['', '#ef4444', '#f59e0b', '#3b82f6', '#10b981', '#059669'][passwordStrength];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const errors: typeof fieldErrors = {};
    let hasError = false;

    if (!password || password.length < 6) {
      errors.password = true;
      hasError = true;
    }
    if (password !== confirmPassword) {
      errors.confirmPassword = true;
      hasError = true;
    }

    setFieldErrors(errors);
    if (hasError) {
      setError(errors.password ? 'Password must be at least 6 characters' : 'Passwords do not match');
      return;
    }

    if (!token) {
      setError('Invalid or missing reset token. Please request a new reset link.');
      return;
    }

    setIsLoading(true);

    try {
      const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5001';
      const response = await fetch(`${API_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token, newPassword: password }),
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.message || 'Failed to reset password');
        return;
      }

      setSuccess(true);
    } catch (error) {
      console.error('Reset password error:', error);
      setError('An error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const inputBaseClass = (hasError: boolean) =>
    `w-full rounded-xl border-2 px-4 py-3.5 text-sm font-medium transition-all duration-200 placeholder:font-normal focus:outline-none focus:ring-0 ${
      hasError
        ? 'border-red-400 bg-red-50/50 text-red-700 placeholder:text-red-400/60 focus:border-red-500 focus:shadow-[0_0_0_3px_rgba(239,68,68,0.1)] dark:border-red-500/50 dark:bg-red-500/5 dark:text-red-400'
        : 'border-gray-200 bg-gray-50/80 text-gray-900 placeholder:text-gray-400 focus:border-indigo-500 focus:bg-white focus:shadow-[0_0_0_3px_rgba(99,102,241,0.12)] dark:border-gray-600 dark:bg-gray-700/50 dark:text-white dark:placeholder:text-gray-500 dark:focus:border-indigo-400'
    }`;

  if (!token) {
    return (
      <div className="relative flex min-h-screen items-center justify-center bg-gray-50 dark:bg-gray-900" style={{ fontFamily: "'Geist', sans-serif" }}>
        <div className="absolute inset-0 z-[1] bg-black text-white [&_canvas]:!w-full">
          <Aurora colorStops={["#6366f1", "#0ea5e9", "#06b6d4"]} amplitude={0.6} blend={0.7} speed={0.3} />
        </div>
        <div className="relative z-[2] w-full max-w-[440px] px-6">
          <div className="rounded-2xl border border-white/10 bg-white/95 p-8 text-center shadow-[0_30px_80px_rgba(0,0,0,0.25)] backdrop-blur-xl dark:border-gray-700/50 dark:bg-gray-800/95 lg:rounded-3xl lg:p-10">
            <div className="mb-6 flex justify-center">
              <Logo className="cursor-default hover:translate-y-0 scale-125" />
            </div>
            <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-red-50 to-orange-50 dark:from-red-500/10 dark:to-orange-500/10">
              <svg className="h-6 w-6 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4.5c-.77-.833-2.694-.833-3.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h2 className="mb-2 text-2xl font-bold text-gray-900 dark:text-white">Invalid Reset Link</h2>
            <p className="mb-6 text-sm text-gray-500 dark:text-gray-400">
              This password reset link is invalid or has expired. Please request a new one.
            </p>
            <Link
              to="/forgot-password"
              className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-indigo-500 to-purple-500 px-6 py-3 text-sm font-bold uppercase tracking-wider text-white transition-all hover:-translate-y-0.5 hover:shadow-[0_12px_35px_rgba(99,102,241,0.35)]"
            >
              Request New Link
            </Link>
            <div className="mt-4">
              <Link to="/" className="text-sm font-semibold text-indigo-500 hover:text-indigo-600 dark:text-indigo-400">
                Back to Sign In
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative flex min-h-screen items-center justify-center bg-gray-50 dark:bg-gray-900" style={{ fontFamily: "'Geist', sans-serif" }}>
      {/* Aurora Background */}
      <div className="absolute inset-0 z-[1] bg-black text-white [&_canvas]:!w-full">
        <Aurora
          colorStops={["#6366f1", "#0ea5e9", "#06b6d4"]}
          amplitude={0.6}
          blend={0.7}
          speed={0.3}
        />
      </div>

      {/* Content */}
      <div
        className="relative z-[2] w-full max-w-[440px] px-6"
        style={{
          opacity: mounted ? 1 : 0,
          transform: mounted ? 'translateY(0) scale(1)' : 'translateY(20px) scale(0.98)',
          transition: 'all 0.7s cubic-bezier(0.16, 1, 0.3, 1)',
        }}
      >
        <div className="rounded-2xl border border-white/10 bg-white/95 p-8 shadow-[0_30px_80px_rgba(0,0,0,0.25)] backdrop-blur-xl dark:border-gray-700/50 dark:bg-gray-800/95 lg:rounded-3xl lg:p-10">
          <div className="mb-6 flex justify-center">
            <Logo className="cursor-default hover:translate-y-0 scale-125" />
          </div>

          {!success ? (
            <>
              <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-indigo-500/10 dark:to-purple-500/10">
                <svg className="h-6 w-6 text-indigo-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>

              <h2 className="mb-1 text-center text-2xl font-bold tracking-[-0.5px] text-gray-900 dark:text-white">
                Set New Password
              </h2>
              <p className="mb-8 text-center text-sm text-gray-500 dark:text-gray-400">
                Choose a strong password for your account
              </p>

              {error && (
                <div
                  className="mb-6 flex items-center gap-3 rounded-xl bg-red-50 px-4 py-3 dark:bg-red-500/10"
                  style={{ animation: 'authShake 0.4s ease-in-out' }}
                >
                  <div className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-red-100 dark:bg-red-500/20">
                    <svg className="h-3 w-3 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <p className="text-sm font-medium text-red-600 dark:text-red-400">{error}</p>
                </div>
              )}

              <form onSubmit={handleSubmit}>
                {/* New Password */}
                <div className="mb-4">
                  <label htmlFor="password" className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    New Password
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      id="password"
                      placeholder="Min. 6 characters"
                      value={password}
                      onChange={(e) => {
                        setPassword(e.target.value);
                        setFieldErrors(prev => ({ ...prev, password: false }));
                        setError('');
                      }}
                      aria-label="New password"
                      className={`${inputBaseClass(!!fieldErrors.password)} pr-12`}
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 rounded-lg p-1.5 text-gray-400 transition-colors hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? <FaEyeSlash size={14} /> : <FaEye size={14} />}
                    </button>
                  </div>
                  {password && (
                    <div className="mt-2.5 flex items-center gap-3">
                      <div className="flex flex-1 gap-1">
                        {[1, 2, 3, 4, 5].map((level) => (
                          <div
                            key={level}
                            className="h-[3px] flex-1 rounded-full transition-all duration-500"
                            style={{
                              backgroundColor: level <= passwordStrength ? strengthColor : 'rgba(0,0,0,0.08)',
                            }}
                          />
                        ))}
                      </div>
                      <span className="text-[11px] font-semibold" style={{ color: strengthColor }}>
                        {strengthLabel}
                      </span>
                    </div>
                  )}
                </div>

                {/* Confirm Password */}
                <div className="mb-6">
                  <label htmlFor="confirmPassword" className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    Confirm Password
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      id="confirmPassword"
                      placeholder="Re-enter your password"
                      value={confirmPassword}
                      onChange={(e) => {
                        setConfirmPassword(e.target.value);
                        setFieldErrors(prev => ({ ...prev, confirmPassword: false }));
                        setError('');
                      }}
                      aria-label="Confirm password"
                      className={`${inputBaseClass(!!fieldErrors.confirmPassword)} pr-12`}
                      required
                    />
                    {confirmPassword && password && (
                      <div className="absolute right-3 top-1/2 -translate-y-1/2">
                        {confirmPassword === password ? (
                          <div className="flex h-5 w-5 items-center justify-center rounded-full bg-emerald-100 dark:bg-emerald-500/20">
                            <FaCheck size={9} className="text-emerald-600 dark:text-emerald-400" />
                          </div>
                        ) : (
                          <div className="flex h-5 w-5 items-center justify-center rounded-full bg-red-100 dark:bg-red-500/20">
                            <svg className="h-2.5 w-2.5 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                            </svg>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  {confirmPassword && password && confirmPassword !== password && (
                    <p className="mt-1.5 text-xs font-medium text-red-500">Passwords don't match</p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="group relative flex w-full items-center justify-center gap-2 overflow-hidden rounded-xl border-none px-6 py-4 text-sm font-bold uppercase tracking-wider text-white transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_12px_35px_rgba(99,102,241,0.35)] active:translate-y-0 disabled:cursor-not-allowed disabled:opacity-50"
                  style={{
                    background: 'linear-gradient(135deg, #6366f1, #8b5cf6, #a855f7)',
                  }}
                >
                  <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/15 to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" style={{ transform: 'skewX(-20deg)' }} />
                  {isLoading ? (
                    <>
                      <div className="h-4 w-4 rounded-full border-2 border-white/30 border-t-white" style={{ animation: 'spin 0.6s linear infinite' }} />
                      <span>Resetting...</span>
                    </>
                  ) : (
                    <span>Reset Password</span>
                  )}
                </button>
              </form>
            </>
          ) : (
            /* Success State */
            <div className="text-center">
              <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-emerald-50 to-green-50 dark:from-emerald-500/10 dark:to-green-500/10">
                <svg className="h-6 w-6 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h2 className="mb-2 text-2xl font-bold tracking-[-0.5px] text-gray-900 dark:text-white">
                Password Reset!
              </h2>
              <p className="mb-6 text-sm text-gray-500 dark:text-gray-400">
                Your password has been updated successfully. You can now sign in with your new password.
              </p>
              <button
                onClick={() => navigate('/')}
                className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-indigo-500 to-purple-500 px-6 py-3 text-sm font-bold uppercase tracking-wider text-white transition-all hover:-translate-y-0.5 hover:shadow-[0_12px_35px_rgba(99,102,241,0.35)]"
              >
                Sign In
              </button>
            </div>
          )}

          {/* Back to Login */}
          {!success && (
            <div className="mt-6 text-center">
              <Link
                to="/"
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-indigo-500 transition-colors hover:text-indigo-600 dark:text-indigo-400 dark:hover:text-indigo-300"
              >
                <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                Back to Sign In
              </Link>
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes authShake {
          0%, 100% { transform: translateX(0); }
          20% { transform: translateX(-6px); }
          40% { transform: translateX(6px); }
          60% { transform: translateX(-3px); }
          80% { transform: translateX(3px); }
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default ResetPassword;
